import { useEffect, useState } from 'react';
import { getEtiquetasDgt, isComerciales } from '../../../../../../helpers/carline-helpers';
import { Carline, SalesgroupWithCarline } from '../../../../../../types/vehicle-types';

function getLateralImage(option: Carline | SalesgroupWithCarline): string | undefined {
	return (
		option.mediaAssets?.D6Turntable3 ||
		option.mediaAssets?.Side_Left ||
		option.mediaAssets?.D6Explore2Alpha ||
		option?.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		option.mediaAssets?.D6MOFASideCV ||
		option.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		option.mediaAssets?.D6MOFASideCV ||
		undefined
	);
}

function getImage(option: Carline | SalesgroupWithCarline): string | undefined {
	return (
		option.mediaAssets?.D6Explore1Alpha ||
		option.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		undefined
	);
}

// Precarga una imagen nativa y notifica su estado de carga.
function preloadImage(src: string | undefined, setLoaded: (loaded: boolean) => void): (() => void) | undefined {
	if (!src) {
		setLoaded(false);
		return undefined;
	}

	const image = new window.Image();
	image.src = src;

	if (image.complete) {
		setLoaded(true);
		return undefined;
	}

	setLoaded(false);
	image.onload = () => setLoaded(true);
	image.onerror = () => setLoaded(false);

	return () => {
		image.onload = null;
		image.onerror = null;
	};
}

interface UseVehicleImageProps {
	option?: Carline | SalesgroupWithCarline;
	image?: string;
	lateralImage?: string;
	etiquetas?: string[];
	loadLateralImage?: boolean;
}

interface DisplayImageProps {
	lateral?: boolean;
}

export const useVehicleImage = ({
	option,
	image,
	lateralImage,
	etiquetas,
	loadLateralImage = false,
}: UseVehicleImageProps) => {
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isLateralImageLoaded, setIsLateralImageLoaded] = useState(false);

	const displayableImage = image ?? (option ? getImage(option) : undefined);
	const displayableLateralImage = loadLateralImage
		? (lateralImage ?? (option ? getLateralImage(option) : undefined))
		: undefined;

	const TURISMOS_PLACEHOLDER =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/car_silhouette.png';

	const TURISMOS_LATERAL_PLACEHOLDER =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/car_lateral_silhouette.png';

	const COMERCIALES_PLACEHOLDER =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/van_silhouette.png';

	const COMERCIALES_LATERAL_PLACEHOLDER =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/van_lateral_silhouette.png';

	// Solo la marca N utiliza el placeholder comercial.
	const comerciales = isComerciales();

	// Placeholder de 3/4 para el render inicial.
	const placeholder = comerciales ? COMERCIALES_PLACEHOLDER : TURISMOS_PLACEHOLDER;

	// Placeholder lateral al pulsar el vehículo.
	const lateralPlaceholder = comerciales ? COMERCIALES_LATERAL_PLACEHOLDER : TURISMOS_LATERAL_PLACEHOLDER;

	const displayableEtiquetas = etiquetas ?? getEtiquetasDgt(option?.etiquetasDGT ?? []);

	useEffect(() => {
		const cleanupImage = preloadImage(displayableImage, setIsImageLoaded);

		if (!loadLateralImage) {
			setIsLateralImageLoaded(false);
			return cleanupImage;
		}

		const cleanupLateralImage = preloadImage(displayableLateralImage, setIsLateralImageLoaded);

		return () => {
			cleanupImage?.();
			cleanupLateralImage?.();
		};
	}, [displayableImage, displayableLateralImage, loadLateralImage]);

	const displayImage = ({ lateral = false }: DisplayImageProps) => {
		if (lateral) {
			return displayableLateralImage && isLateralImageLoaded ? displayableLateralImage : lateralPlaceholder;
		}

		return displayableImage && isImageLoaded ? displayableImage : placeholder;
	};

	return {
		displayImage,
		etiquetas: displayableEtiquetas,
		setIsImageLoaded,
		setIsLateralImageLoaded,
	};
};
